import { useState } from 'react';
import { getAppointments, updateAppointment, deleteAppointment, updatePatientFields } from '../api';
import { timeToMinutes, addMinutesToTime } from '../utils/turnoTiempo';

// Lógica de edición/cancelación de turnos compartida entre Calendario.jsx y
// PendientesDelDia.jsx. No hay series_id en el backend: "serie" se infiere en
// runtime trayendo todos los turnos del paciente y separando por fecha.
//
// `pacientes`/`onPacienteActualizado` son opcionales — solo hacen falta para
// el flujo de renombrar (ver iniciarRenombrar más abajo). Sin ellos, el resto
// del hook funciona igual que antes.
export default function useAccionesTurno({ citas, setCitas, onError, pacientes, onPacienteActualizado }) {
  const fetchTurnosPaciente = async (patientId) => getAppointments({ patient: patientId });

  // ── Edición simple ──
  const [editModal, setEditModal] = useState(null);
  const [editFecha, setEditFecha] = useState('');
  const [editHora, setEditHora] = useState('');
  const [editDuracion, setEditDuracion] = useState(60);
  const [editNotas, setEditNotas] = useState('');
  const [editTitulo, setEditTitulo] = useState('');
  const [editGuardando, setEditGuardando] = useState(false);

  const abrirEdicion = (cita) => {
    setEditFecha(cita.date);
    setEditHora(cita.time ? cita.time.slice(0, 5) : '');
    setEditDuracion(cita.duration || 60);
    setEditNotas(cita.notes || '');
    setEditTitulo(cita.title || '');
    setEditModal(cita);
  };

  const cerrarEdicion = () => setEditModal(null);

  // ── Edición con 3 opciones de serie ──
  const [editPendiente, setEditPendiente] = useState(null);

  const aplicarEdicionSerie = async (items, payload, originalFecha, originalHora) => {
    const deltaDias = payload.date
      ? Math.round((new Date(payload.date) - new Date(originalFecha)) / (1000 * 60 * 60 * 24))
      : 0;
    const deltaMin = (payload.time !== undefined && originalHora && payload.time)
      ? timeToMinutes(payload.time) - timeToMinutes(originalHora) : 0;

    return await Promise.all(items.map(c => {
      const p = {};
      if (deltaDias !== 0) {
        const d = new Date(c.date + 'T12:00:00');
        d.setDate(d.getDate() + deltaDias);
        p.date = d.toISOString().split('T')[0];
      }
      if (payload.time !== undefined) {
        const cTime = c.time ? c.time.slice(0, 5) : null;
        p.time = (deltaMin !== 0 && cTime) ? addMinutesToTime(cTime, deltaMin) : payload.time;
      }
      if (payload.duration !== undefined) p.duration = payload.duration;
      if (payload.notes !== undefined) p.notes = payload.notes;
      return updateAppointment(c.id, p);
    }));
  };

  const guardarEdit = async () => {
    if (!editModal || editGuardando) return;
    setEditGuardando(true);

    const fechaCambio = editFecha !== editModal.date;
    const horaCambio = editHora !== (editModal.time?.slice(0, 5) || '');
    const duracionCambio = Number(editDuracion) !== (editModal.duration ?? 60);
    const notasCambio = editNotas !== (editModal.notes || '');
    // Título solo aplica a eventos genéricos (sin paciente) — para un turno
    // de paciente el "nombre" es Patient.first_name/last_name, ver
    // iniciarRenombrar más abajo, nunca este campo.
    const tituloCambio = !editModal.patient && editTitulo !== (editModal.title || '');

    const payload = {};
    if (fechaCambio) payload.date = editFecha;
    if (horaCambio) payload.time = editHora || null;
    if (duracionCambio) payload.duration = Number(editDuracion);
    if (notasCambio) payload.notes = editNotas;
    if (tituloCambio) payload.title = editTitulo;

    if (Object.keys(payload).length === 0) {
      setEditModal(null); setEditGuardando(false); return;
    }

    const haySerieConcierne = editModal.patient && (fechaCambio || horaCambio || duracionCambio);

    if (!haySerieConcierne) {
      try {
        const updated = await updateAppointment(editModal.id, payload);
        setCitas(prev => prev.map(c => c.id === editModal.id ? updated : c));
      } catch (err) { onError('Error al guardar: ' + err.message); }
      setEditModal(null); setEditGuardando(false); return;
    }

    let todosDelPaciente;
    try { todosDelPaciente = await fetchTurnosPaciente(editModal.patient); }
    catch {
      try {
        const updated = await updateAppointment(editModal.id, payload);
        setCitas(prev => prev.map(c => c.id === editModal.id ? updated : c));
      } catch (err) { onError('Error al guardar: ' + err.message); }
      setEditModal(null); setEditGuardando(false); return;
    }

    const futuros = todosDelPaciente.filter(c => c.id !== editModal.id && c.date >= editModal.date);
    const todos = todosDelPaciente.filter(c => c.id !== editModal.id);

    if (futuros.length === 0) {
      try {
        const updated = await updateAppointment(editModal.id, payload);
        setCitas(prev => prev.map(c => c.id === editModal.id ? updated : c));
      } catch (err) { onError('Error al guardar: ' + err.message); }
      setEditModal(null); setEditGuardando(false); return;
    }

    setEditPendiente({
      citaId: editModal.id,
      payload,
      originalFecha: editModal.date,
      originalHora: editModal.time?.slice(0, 5) || null,
      futuros,
      todos,
      nombrePaciente: editModal.patient_name || 'este paciente',
    });
    setEditModal(null);
    setEditGuardando(false);
  };

  const cerrarEditPendiente = () => setEditPendiente(null);

  const editarSoloEste = async () => {
    if (!editPendiente) return;
    try {
      const updated = await updateAppointment(editPendiente.citaId, editPendiente.payload);
      setCitas(prev => prev.map(c => c.id === editPendiente.citaId ? updated : c));
    } catch (err) { onError('Error: ' + err.message); }
    setEditPendiente(null);
  };

  const editarEstoYSiguientes = async () => {
    if (!editPendiente) return;
    const { citaId, payload, originalFecha, originalHora, futuros } = editPendiente;
    const citaActual = citas.find(c => c.id === citaId) || { id: citaId, date: originalFecha, time: originalHora ? originalHora + ':00' : null };
    try {
      const actualizados = await aplicarEdicionSerie([citaActual, ...futuros], payload, originalFecha, originalHora);
      const mapa = Object.fromEntries(actualizados.filter(Boolean).map(c => [c.id, c]));
      setCitas(prev => prev.map(c => mapa[c.id] || c));
    } catch (err) { onError('Error: ' + err.message); }
    setEditPendiente(null);
  };

  const editarTodaLaSerie = async () => {
    if (!editPendiente) return;
    const { citaId, payload, originalFecha, originalHora, todos } = editPendiente;
    const citaActual = citas.find(c => c.id === citaId) || { id: citaId, date: originalFecha, time: originalHora ? originalHora + ':00' : null };
    try {
      const actualizados = await aplicarEdicionSerie([citaActual, ...todos], payload, originalFecha, originalHora);
      const mapa = Object.fromEntries(actualizados.filter(Boolean).map(c => [c.id, c]));
      setCitas(prev => prev.map(c => mapa[c.id] || c));
    } catch (err) { onError('Error: ' + err.message); }
    setEditPendiente(null);
  };

  // ── Cancelación (soft-delete) ──
  const [cancelPendiente, setCancelPendiente] = useState(null);
  const [cancelando, setCancelando] = useState(false);
  const [confirmEliminar, setConfirmEliminar] = useState(null);

  const iniciarCancelar = async (citaId) => {
    const cita = citas.find(c => c.id === citaId);
    if (!cita) return;
    if (!cita.patient) { setConfirmEliminar(citaId); return; }
    setCancelando(true);
    try {
      const todosDelPaciente = await fetchTurnosPaciente(cita.patient);
      const futuros = todosDelPaciente.filter(c => c.id !== citaId && c.date >= cita.date);
      const todos = todosDelPaciente.filter(c => c.id !== citaId);
      if (futuros.length === 0) {
        setConfirmEliminar(citaId);
      } else {
        setCancelPendiente({ citaId, patientId: cita.patient, futuros, todos, nombrePaciente: cita.patient_name || 'este paciente' });
      }
    } catch { setConfirmEliminar(citaId); }
    finally { setCancelando(false); }
  };

  const cerrarCancelPendiente = () => setCancelPendiente(null);

  const cancelarSoloEste = async () => {
    if (!cancelPendiente) return;
    try {
      await deleteAppointment(cancelPendiente.citaId);
      setCitas(prev => prev.filter(c => c.id !== cancelPendiente.citaId));
    } catch (err) { onError('Error: ' + err.message); }
    setCancelPendiente(null);
  };

  const cancelarEstoYSiguientes = async () => {
    if (!cancelPendiente) return;
    const ids = [cancelPendiente.citaId, ...cancelPendiente.futuros.map(f => f.id)];
    try {
      await Promise.all(ids.map(id => deleteAppointment(id)));
      setCitas(prev => prev.filter(c => !ids.includes(c.id)));
    } catch (err) { onError('Error: ' + err.message); }
    setCancelPendiente(null);
  };

  const cancelarTodaLaSerie = async () => {
    if (!cancelPendiente) return;
    const ids = [cancelPendiente.citaId, ...cancelPendiente.todos.map(f => f.id)];
    try {
      await Promise.all(ids.map(id => deleteAppointment(id)));
      setCitas(prev => prev.filter(c => !ids.includes(c.id)));
    } catch (err) { onError('Error: ' + err.message); }
    setCancelPendiente(null);
  };

  const cerrarConfirmEliminar = () => setConfirmEliminar(null);

  const confirmarEliminarCita = async () => {
    try {
      await deleteAppointment(confirmEliminar);
      setCitas(prev => prev.filter(c => c.id !== confirmEliminar));
    } catch (err) { onError('Error: ' + err.message); }
    setConfirmEliminar(null);
  };

  // ── Renombrar (lápiz en TurnoPopover/EditarTurnoModal) ──
  // Un evento genérico se renombra editando Appointment.title (mismo modal
  // de edición de siempre, ver abrirEdicion/guardarEdit arriba) — un turno
  // de paciente renombra el registro maestro del paciente
  // (Patient.first_name/last_name), no una etiqueta local del turno, así que
  // necesita su propio flujo de 2 pasos (form + confirmación explícita del
  // alcance real del cambio) en vez de un campo más en EditarTurnoModal.
  const [renombrarPacienteForm, setRenombrarPacienteForm] = useState(null); // { citaId, patientId, nombreActual, apellidoActual } | null
  const [renombrarPacienteConfirmar, setRenombrarPacienteConfirmar] = useState(null); // { ...form, nombre, apellido } | null
  const [renombrarGuardando, setRenombrarGuardando] = useState(false);
  const [renombrarError, setRenombrarError] = useState(null);

  const iniciarRenombrar = (cita) => {
    if (!cita.patient) {
      abrirEdicion(cita);
      return;
    }
    const paciente = (pacientes || []).find(p => p.id === cita.patient);
    setRenombrarError(null);
    setRenombrarPacienteForm({
      citaId: cita.id,
      patientId: cita.patient,
      nombreActual: paciente?.first_name || '',
      apellidoActual: paciente?.last_name || '',
    });
  };

  const cerrarRenombrarPaciente = () => {
    setRenombrarPacienteForm(null);
    setRenombrarPacienteConfirmar(null);
    setRenombrarError(null);
  };

  const confirmarRenombrarPaso1 = (nombre, apellido) => {
    if (!renombrarPacienteForm) return;
    setRenombrarPacienteConfirmar({ ...renombrarPacienteForm, nombre, apellido });
  };

  const volverARenombrarPaso1 = () => setRenombrarPacienteConfirmar(null);

  const confirmarRenombrarPaso2 = async () => {
    if (!renombrarPacienteConfirmar) return;
    const { patientId, nombre, apellido } = renombrarPacienteConfirmar;
    const first_name = nombre.trim() || 'Nuevo';
    const last_name = apellido.trim() || 'Paciente';
    setRenombrarGuardando(true);
    setRenombrarError(null);
    try {
      await updatePatientFields(patientId, [
        { field: 'first_name', value: first_name },
        { field: 'last_name', value: last_name },
      ]);
      // patient_name viene denormalizado en cada Appointment (ver auditoría:
      // AppointmentSerializer.get_patient_name) -- el backend no lo
      // recalcula solo, así que hay que parchear acá los turnos de este
      // paciente ya cargados en `citas`, no alcanza con refrescar el array
      // de pacientes del sidebar.
      const nombreCompleto = `${first_name} ${last_name}`.trim();
      setCitas(prev => prev.map(c => (
        c.patient === patientId ? { ...c, patient_name: nombreCompleto } : c
      )));
      if (onPacienteActualizado) {
        const pacienteExistente = (pacientes || []).find(p => p.id === patientId);
        if (pacienteExistente) {
          onPacienteActualizado({
            ...pacienteExistente,
            first_name,
            last_name,
            nombre: `${last_name} ${first_name}`.trim(),
          });
        }
      }
      cerrarRenombrarPaciente();
    } catch (err) {
      setRenombrarError(err.message);
    } finally {
      setRenombrarGuardando(false);
    }
  };

  return {
    fetchTurnosPaciente,
    editModal, editFecha, setEditFecha, editHora, setEditHora, editDuracion, setEditDuracion,
    editNotas, setEditNotas, editTitulo, setEditTitulo, editGuardando, abrirEdicion, cerrarEdicion, guardarEdit,
    editPendiente, editarSoloEste, editarEstoYSiguientes, editarTodaLaSerie, cerrarEditPendiente,
    cancelPendiente, cancelando, iniciarCancelar, cancelarSoloEste, cancelarEstoYSiguientes,
    cancelarTodaLaSerie, cerrarCancelPendiente,
    confirmEliminar, confirmarEliminarCita, cerrarConfirmEliminar,
    iniciarRenombrar, renombrarPacienteForm, renombrarPacienteConfirmar, renombrarGuardando, renombrarError,
    cerrarRenombrarPaciente, confirmarRenombrarPaso1, volverARenombrarPaso1, confirmarRenombrarPaso2,
  };
}
